import React, { useEffect, useLayoutEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AuthenticatorSettings from "Pages/Auth/AuthenticatorSettings";
import DisableAuthenticator from "Pages/Auth/DisableAuthenticator";
import EnableAuthenticator from "Pages/Auth/EnableAuthenticator";
import AccountService from "Services/Account/AccountService";
import { Loader } from "Shared/Common/Loader";
import { setValueIntoSessionStorage } from "Utils/Common/CommonMethods";
import {
  cacheAuthenticatorEnabled,
  readCachedAuthenticatorEnabled,
} from "./authenticatorStatusCache";

const SETTINGS_PATH = "/authenticator-settings";
const SETUP_PATH = "/enable-authenticator";
const DISABLE_PATH = "/disable-authenticator";

const isSetupPath = (pathname: string) =>
  pathname.includes("enable-authenticator") ||
  pathname.includes("verify-authenticator") ||
  pathname.includes("authenticator-setup-success");

const isDisablePath = (pathname: string) =>
  pathname.includes("disable-authenticator");

const isSettingsPath = (pathname: string) =>
  pathname.includes("authenticator-settings");

const isLegacySetupSubPath = (pathname: string) =>
  pathname.includes("verify-authenticator") ||
  pathname.includes("authenticator-setup-success");

const isLegacyDisableSubPath = (pathname: string) =>
  pathname.includes("disable-authenticator-password") ||
  pathname.includes("disable-authenticator-success");

const extractIsEnabled = (payload: any): boolean | null => {
  const candidates = [
    payload?.data?.isEnabled,
    payload?.data?.IsEnabled,
    payload?.isEnabled,
    payload?.IsEnabled,
  ];
  for (const value of candidates) {
    if (typeof value === "boolean") return value;
  }
  return null;
};

const AuthenticatorWrapper: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(false);
  const [checking, setChecking] = useState(true);

  // Reset gate before paint so the next screen never flashes while status API runs.
  useLayoutEffect(() => {
    setChecking(true);
    setAllowed(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;

    const verifyPermission = async () => {
      setValueIntoSessionStorage("pageId", 18);

      const { pathname, state } = location;

      // Keep old URLs working by sending them to the single setup/disable page.
      if (isLegacySetupSubPath(pathname)) {
        if (!cancelled) {
          navigate(SETUP_PATH, {
            replace: true,
            state: state ?? { isEnabled: false },
          });
        }
        return;
      }

      if (isLegacyDisableSubPath(pathname)) {
        if (!cancelled) {
          navigate(DISABLE_PATH, {
            replace: true,
            state: state ?? { isEnabled: true },
          });
        }
        return;
      }

      if (isSettingsPath(pathname)) {
        if (!cancelled) {
          setAllowed(true);
          setChecking(false);
        }
        return;
      }

      const stateEnabled =
        typeof (state as any)?.isEnabled === "boolean"
          ? (state as any).isEnabled
          : null;
      const cachedEnabled = readCachedAuthenticatorEnabled();

      try {
        const res = await AccountService.getAuthenticatorStatus();
        const payload = res?.data;
        let isEnabled = extractIsEnabled(payload);

        if (isEnabled === null) {
          isEnabled = stateEnabled ?? cachedEnabled;
        } else {
          cacheAuthenticatorEnabled(isEnabled);
        }

        if (isEnabled === null) {
          if (!cancelled) navigate(SETTINGS_PATH, { replace: true });
          return;
        }

        const canAccess = isDisablePath(pathname)
          ? isEnabled === true
          : isSetupPath(pathname)
            ? isEnabled === false
            : false;

        if (cancelled) return;

        if (canAccess) {
          setAllowed(true);
        } else {
          navigate(SETTINGS_PATH, { replace: true });
        }
      } catch {
        const fallback = stateEnabled ?? cachedEnabled;
        if (cancelled) return;

        if (fallback === null) {
          navigate(SETTINGS_PATH, { replace: true });
          return;
        }

        const canAccess = isDisablePath(pathname)
          ? fallback === true
          : isSetupPath(pathname)
            ? fallback === false
            : false;

        if (canAccess) {
          setAllowed(true);
        } else {
          navigate(SETTINGS_PATH, { replace: true });
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    verifyPermission();
    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.state, navigate]);

  if (checking || !allowed) {
    return (
      <div
        className="d-flex flex-center flex-column"
        style={{ minHeight: "100vh" }}
      >
        <Loader />
      </div>
    );
  }

  if (location.pathname.includes("disable-authenticator")) {
    return <DisableAuthenticator />;
  }
  if (location.pathname.includes("enable-authenticator")) {
    return <EnableAuthenticator />;
  }
  return <AuthenticatorSettings />;
};

export default AuthenticatorWrapper;
